import type { CheckoutRunResult } from "@/lib/checkout-lock";
import type { CheckoutTimingHooks } from "@/lib/checkout-timing";

/**
 * BuyEventButton's steps after the click: create the session, load Stripe.js,
 * redirect. Each failure sends one `checkout_redirect_failed` and releases
 * the lock. After every await the run stops if it's stale (the lock moved on
 * after the safety timeout or a bfcache restore), so an old session never
 * redirects, navigates or sends an event over a newer attempt.
 *
 * `timing` (optional) only observes: its calls are wrapped, never awaited,
 * and come after the step they time, so it can't delay or change a step.
 */

export interface CheckoutFlowStripe {
  redirectToCheckout(options: {
    sessionId: string;
  }): Promise<{ error?: { type?: string } } | undefined | void>;
}

export interface CheckoutFailure {
  failure_stage: "session_creation" | "stripe_initialization" | "stripe_redirect";
  failure_reason: string;
  stripe_checkout_session_id?: string;
}

export interface CheckoutFlowDeps {
  startSession: () => Promise<{
    errorRedirect?: string;
    sessionId?: string;
    timingToken?: string;
  }>;
  getStripe: () => Promise<CheckoutFlowStripe | null>;
  navigate: (url: string) => void;
  captureFailure: (failure: CheckoutFailure) => void;
  /** Where to go when the action returns neither a session nor an error. */
  missingSessionRedirect: string;
  timing?: CheckoutTimingHooks;
}

const observe = (fn: () => void): void => {
  try {
    fn();
  } catch {
    // Timing must never change the checkout.
  }
};

export async function runCheckoutFlow(
  deps: CheckoutFlowDeps,
  isCurrent: () => boolean,
): Promise<CheckoutRunResult> {
  let failureStage: CheckoutFailure["failure_stage"] = "session_creation";
  let sessionId: string | undefined;
  const fail = (failure: CheckoutFailure) => {
    deps.captureFailure(failure);
    observe(() => deps.timing?.failed(failure.failure_stage));
  };
  try {
    const checkout = await deps.startSession();
    if (!isCurrent()) return "released";
    sessionId = checkout.sessionId;
    if (checkout.errorRedirect) {
      fail({
        failure_stage: "session_creation",
        failure_reason: "error_redirect",
      });
      deps.navigate(checkout.errorRedirect);
      return "released";
    }
    if (!sessionId) {
      fail({
        failure_stage: "session_creation",
        failure_reason: "missing_session_id",
      });
      deps.navigate(deps.missingSessionRedirect);
      return "released";
    }
    const returnedSessionId = sessionId;
    observe(() =>
      deps.timing?.sessionReturned(returnedSessionId, checkout.timingToken),
    );

    failureStage = "stripe_initialization";
    const stripe = await deps.getStripe();
    if (!isCurrent()) return "released";
    if (!stripe) {
      fail({
        failure_stage: "stripe_initialization",
        failure_reason: "stripe_unavailable",
        stripe_checkout_session_id: sessionId,
      });
      return "released";
    }
    observe(() => deps.timing?.stripeLoaded());

    failureStage = "stripe_redirect";
    const redirect = stripe.redirectToCheckout({ sessionId });
    observe(() => deps.timing?.redirectCalled());
    const redirectResult = await redirect;
    if (!isCurrent()) return "released";
    if (redirectResult?.error) {
      fail({
        failure_stage: "stripe_redirect",
        failure_reason: redirectResult.error.type || "stripe_error",
        stripe_checkout_session_id: sessionId,
      });
      return "released";
    }
    // Leaving for Stripe: keep every button disabled until the page goes,
    // comes back from the bfcache, or the safety timeout ends.
    return "redirecting";
  } catch {
    if (!isCurrent()) return "released";
    // A thrown action (network drop, aborted request) used to leave the
    // button spinning forever.
    fail({
      failure_stage: failureStage,
      failure_reason: "thrown_error",
      stripe_checkout_session_id: sessionId,
    });
    return "released";
  }
}
